import React, { createContext, useContext, useRef, RefObject } from 'react';

/**
 * Context for providing a portal container that respects UI scaling.
 *
 * Dropdowns and other floating elements should portal into this container
 * instead of document.body to ensure they scale with the rest of the UI.
 */

interface PortalContainerContextValue {
  /** Reference to the portal container element inside the scaled wrapper */
  portalContainerRef: RefObject<HTMLDivElement>;
  /** Get the portal container element, falling back to document.body */
  getPortalContainer: () => HTMLElement;
}

const PortalContainerContext = createContext<PortalContainerContextValue | null>(null);

export const PortalContainerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const portalContainerRef = useRef<HTMLDivElement>(null);

  const getPortalContainer = (): HTMLElement => {
    return portalContainerRef.current || document.body;
  };

  return (
    <PortalContainerContext.Provider value={{ portalContainerRef, getPortalContainer }}>
      {children}
      {/* This div will be moved to Layout.tsx inside the scaled wrapper */}
    </PortalContainerContext.Provider>
  );
};

/**
 * Hook to access the portal container for rendering floating elements.
 *
 * @example
 * const { getPortalContainer } = usePortalContainer();
 * return createPortal(<Dropdown />, getPortalContainer());
 */
export function usePortalContainer(): PortalContainerContextValue {
  const context = useContext(PortalContainerContext);

  if (!context) {
    // Fallback for components rendered outside the provider
    return {
      portalContainerRef: { current: null },
      getPortalContainer: () => document.body,
    };
  }

  return context;
}

export { PortalContainerContext };
